import type { Column, FilterFn } from "@tanstack/react-table";
import type { DataTableColumnMeta } from "./DataTable.types";

/**
 * TanStack global filter: case-insensitive substring over `globalSearchKeys`, or all values if unset.
 */
export function buildGlobalFilterFn<TData>(
  keys?: (keyof TData & string)[]
): FilterFn<TData> {
  return (row, _columnId, filterValue) => {
    if (filterValue === undefined || filterValue === null) return true;
    const q = String(filterValue).trim().toLowerCase();
    if (!q) return true;
    const original = row.original as Record<string, unknown>;
    if (keys && keys.length > 0) {
      return keys.some((k) =>
        String(original[k as string] ?? "")
          .toLowerCase()
          .includes(q)
      );
    }
    return Object.values(original).some((val) =>
      String(val ?? "")
        .toLowerCase()
        .includes(q)
    );
  };
}

/** Label for column visibility UI — reads `meta.label` or falls back to `column.id`. */
export function dataTableColumnLabel<TData>(column: Column<TData>): string {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  return meta?.label ?? column.id;
}

/** Default column sizing for `useReactTable` when columns omit sizes. */
export const dataTableDefaultColumnSizing = {
  minSize: 96,
  size: 180,
} as const;
